import api from './apiService';
import { parseTOC, flattenNavigation } from '../utils/markdown-parser';

// Session storage keys for caching
const CACHE_KEYS = {
  TOC: 'docs_toc_cache',
  NAVIGATION: 'docs_navigation_cache',
  DOCUMENTS: 'docs_documents_cache',
  CACHE_TIMESTAMP: 'docs_cache_timestamp'
};

// Cache expiry time (1 hour)
const CACHE_EXPIRY_MS = 60 * 60 * 1000;

// Background preloading state
let backgroundPreloadingInProgress = false;

// Shared image cache for both preloading and rendering
const imageCache = {
  cache: new Map(),

  // Store blob and create object URL
  store: async function(key, blob) {
    try {
      // Verify blob is valid
      if (!blob || blob.size === 0) {
        throw new Error('Invalid or empty blob');
      }

      // Process base64 data if needed
      let properBlob = blob;
      try {
        const text = await blob.text();
        if (text.startsWith('iVBORw0K') || text.startsWith('data:image/')) {
          // This is base64 data, decode it properly
          let base64Data = text;
          if (text.startsWith('data:image/')) {
            // Remove data URL prefix
            base64Data = text.split(',')[1];
          }

          // Convert base64 to binary
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          // Create proper blob from binary data
          properBlob = new Blob([bytes], { type: blob.type });

          if (process.env.NODE_ENV === 'development') {
            console.log(`DocumentationService ImageCache: Decoded base64 image: ${key}`);
          }
        }
      } catch (decodeError) {
        console.warn(`DocumentationService ImageCache: Failed to decode base64 for ${key}, using original blob:`, decodeError);
      }

      // Create object URL directly from the blob
      const objectUrl = URL.createObjectURL(properBlob);

      // Store both blob and URL
      this.cache.set(key, {
        blob: properBlob,
        objectUrl: objectUrl,
        timestamp: Date.now(),
        mimeType: properBlob.type
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`DocumentationService ImageCache: Stored blob for ${key} - size: ${properBlob.size} bytes`);
      }

      return objectUrl;
    } catch (error) {
      console.error(`DocumentationService ImageCache: Error storing blob for ${key}:`, error);
      throw error;
    }
  },

  // Get cached object URL
  get: function(key) {
    const entry = this.cache.get(key);
    if (entry && entry.objectUrl) {
      return entry.objectUrl;
    }
    return null;
  },

  // Check if key exists in cache
  has: function(key) {
    return this.cache.has(key);
  },

  // Clean up all resources
  cleanup: function() {
    this.cache.forEach((entry, key) => {
      if (entry.objectUrl) {
        URL.revokeObjectURL(entry.objectUrl);
      }
    });
    this.cache.clear();
    if (process.env.NODE_ENV === 'development') {
      console.log('DocumentationService ImageCache: Cleaned up all image resources');
    }
  }
};

const documentationService = {
  /**
   * Check if cache is valid (not expired)
   */
  isCacheValid: () => {
    try {
      const timestamp = sessionStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
      if (!timestamp) return false;

      const cacheAge = Date.now() - parseInt(timestamp);
      return cacheAge < CACHE_EXPIRY_MS;
    } catch (error) {
      console.warn('Error checking cache validity:', error);
      return false;
    }
  },

  /**
   * Clear all documentation cache
   */
  clearCache: () => {
    try {
      Object.values(CACHE_KEYS).forEach(key => {
        sessionStorage.removeItem(key);
      });
      // Clear image cache as well
      imageCache.cleanup();
      // Reset background preloading flag
      backgroundPreloadingInProgress = false;
      if (process.env.NODE_ENV === 'development') {
        console.log('Documentation cache cleared');
      }
    } catch (error) {
      console.warn('Error clearing documentation cache:', error);
    }
  },

  /**
   * Get the shared image cache for use by DocumentationPage
   */
  getImageCache: () => imageCache,

  /**
   * Background preload all documentation pages and their images
   * This is called when user first navigates to documentation to preload everything
   */
  backgroundPreloadAllDocumentation: async () => {
    // Prevent duplicate background preloading
    if (backgroundPreloadingInProgress) {
      if (process.env.NODE_ENV === 'development') {
        console.log('📚 Background preloading already in progress, skipping...');
      }
      return;
    }

    try {
      backgroundPreloadingInProgress = true;

      if (process.env.NODE_ENV === 'development') {
        console.log('📚 Starting background preload of all documentation...');
      }

      // Get the navigation structure to find all documents
      const navigation = await documentationService.fetchTOC();

      // Extract all document URLs from the navigation structure
      const allDocUrls = [];

      const extractUrls = (items) => {
        items.forEach(item => {
          if (item.url && !allDocUrls.includes(item.url)) {
            allDocUrls.push(item.url);
          }
          if (item.children && item.children.length > 0) {
            extractUrls(item.children);
          }
          if (item.items && item.items.length > 0) {
            extractUrls(item.items);
          }
        });
      };

      navigation.forEach(category => {
        if (category.items) {
          extractUrls(category.items);
        }
      });

      // Remove already preloaded documents (index.md and any others from login preloading)
      const documentsToPreload = allDocUrls.filter(url => {
        const cached = documentationService.getCachedDocument(url);
        return !cached; // Only preload if not already cached
      });

      if (documentsToPreload.length === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📚 All documentation already cached, no background preloading needed');
        }
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`📚 Background preloading ${documentsToPreload.length} documents:`, documentsToPreload);
      }

      // Preload documents in batches to avoid overwhelming the server
      const batchSize = 3;
      const batches = [];
      for (let i = 0; i < documentsToPreload.length; i += batchSize) {
        batches.push(documentsToPreload.slice(i, i + batchSize));
      }

      // Process batches with delays
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Process documents in current batch in parallel
        const batchPromises = batch.map(async (docUrl, docIndex) => {
          try {
            // Small delay between documents in the same batch
            await new Promise(resolve => setTimeout(resolve, docIndex * 100));

            // Fetch and cache the document
            const docData = await documentationService.fetchDocumentationWithFrontMatter(docUrl);

            // Extract and preload images from this document
            const imagePaths = documentationService.extractImagePaths(docData.content);

            if (imagePaths.length > 0) {
              // Preload images for this document
              const imagePromises = imagePaths.map(async (imagePath, imageIndex) => {
                try {
                  // Small delay between images
                  await new Promise(resolve => setTimeout(resolve, imageIndex * 50));

                  // Only preload if not already cached
                  if (!imageCache.has(imagePath)) {
                    const blob = await documentationService.fetchDocumentationImage(imagePath);
                    await imageCache.store(imagePath, blob);

                    if (process.env.NODE_ENV === 'development') {
                      console.log(`📚 Background preloaded image: ${imagePath}`);
                    }
                  }
                } catch (error) {
                  console.warn(`📚 Failed to background preload image ${imagePath}:`, error.message);
                }
              });

              await Promise.all(imagePromises);
            }

            if (process.env.NODE_ENV === 'development') {
              console.log(`📚 Background preloaded document: ${docUrl} (${imagePaths.length} images)`);
            }

          } catch (error) {
            console.warn(`📚 Failed to background preload document ${docUrl}:`, error.message);
          }
        });

        await Promise.all(batchPromises);

        // Delay between batches to avoid overwhelming the server
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('📚 Background preloading of all documentation completed');
      }

    } catch (error) {
      console.warn('📚 Background documentation preloading failed:', error.message);
      // Don't throw - background preloading is optional and shouldn't affect the user experience
    } finally {
      backgroundPreloadingInProgress = false;
    }
  },

  /**
   * Get cached document
   */
  getCachedDocument: (filename) => {
    try {
      if (!documentationService.isCacheValid()) return null;

      const cache = sessionStorage.getItem(CACHE_KEYS.DOCUMENTS);
      if (!cache) return null;

      const documents = JSON.parse(cache);
      return documents[filename] || null;
    } catch (error) {
      console.warn('Error getting cached document:', error);
      return null;
    }
  },

  /**
   * Cache document
   */
  cacheDocument: (filename, data) => {
    try {
      const cache = sessionStorage.getItem(CACHE_KEYS.DOCUMENTS);
      const documents = cache ? JSON.parse(cache) : {};

      documents[filename] = {
        ...data,
        cachedAt: Date.now()
      };

      sessionStorage.setItem(CACHE_KEYS.DOCUMENTS, JSON.stringify(documents));
      sessionStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());

      if (process.env.NODE_ENV === 'development') {
        console.log(`Cached document: ${filename}`);
      }
    } catch (error) {
      console.warn('Error caching document:', error);
    }
  },

  /**
   * Fetch documentation file from the API
   * @param {string} filename - Optional filename to fetch specific documentation (should include .md extension)
   * @returns {Promise<string>} Markdown content as string
   */
  fetchDocumentation: async (filename = '') => {
    try {
      const url = filename ? `/docs/${filename}` : '/docs';

      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching documentation from: ${url}`);
        console.log('API base URL:', api.defaults.baseURL);

      }

      const response = await api.get(url);

      if (process.env.NODE_ENV === 'development') {
        console.log('Documentation response type:', typeof response.data);
        console.log('Response status:', response.status);
        console.log('Content-Type:', response.headers['content-type']);
      }

      // API now returns markdown content directly as string
      if (typeof response.data === 'string') {
        return response.data;
      } else {
        throw new Error('Invalid response format: expected markdown content as string');
      }

    } catch (error) {
      console.error('Error fetching documentation:', error);

      // Handle specific error cases
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;

        switch (status) {
          case 401:
            throw new Error('Authentication required to access documentation');
          case 403:
            throw new Error('You do not have permission to access this documentation');
          case 404:
            throw new Error('Documentation file not found');
          case 400:
            throw new Error('Only markdown (.md) files are supported');
          default:
            throw new Error(`Failed to load documentation: ${message}`);
        }
      } else if (error.request) {
        throw new Error('Network error: Unable to connect to documentation service');
      } else {
        throw new Error(`Documentation service error: ${error.message}`);
      }
    }
  },

  /**
   * Fetch documentation with parsed front matter
   * @param {string} filename - Optional filename to fetch specific documentation
   * @returns {Promise<Object>} { frontMatter, content }
   */
  fetchDocumentationWithFrontMatter: async (filename = '') => {
    try {
      // Check cache first
      const cached = documentationService.getCachedDocument(filename);
      if (cached) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Using cached document: ${filename}`);
        }
        return cached;
      }

      const url = filename ? `/docs/${filename}?parse=true` : '/docs?parse=true';

      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching parsed documentation from: ${url}`);
      }

      const response = await api.get(url);

      if (process.env.NODE_ENV === 'development') {
        console.log('Parsed documentation response:', response.data);
      }

      // API returns JSON with frontMatter and content when parse=true
      if (response.data && typeof response.data === 'object') {
        // Cache the document
        documentationService.cacheDocument(filename, response.data);
        return response.data;
      } else {
        throw new Error('Invalid response format: expected JSON with frontMatter and content');
      }
    } catch (error) {
      console.error('Error fetching documentation with front matter:', error);
      throw error;
    }
  },

  /**
   * Fetch and parse TOC.md to build navigation structure
   * @returns {Promise<Array>} Navigation structure
   */
  fetchTOC: async () => {
    try {
      // Check for cached navigation first
      if (documentationService.isCacheValid()) {
        const cachedNav = sessionStorage.getItem(CACHE_KEYS.NAVIGATION);
        if (cachedNav) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Using cached TOC navigation');
          }
          return JSON.parse(cachedNav);
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Fetching TOC.md with front matter...');
      }

      const { frontMatter, content } = await documentationService.fetchDocumentationWithFrontMatter('TOC.md');

      if (process.env.NODE_ENV === 'development') {
        console.log('TOC.md front matter:', frontMatter);
        console.log('TOC.md content received:', content.substring(0, 200) + '...');
      }

      const navigation = parseTOC(content);

      if (process.env.NODE_ENV === 'development') {
        console.log('Parsed TOC navigation:', navigation);
      }

      // Cache the navigation
      try {
        sessionStorage.setItem(CACHE_KEYS.NAVIGATION, JSON.stringify(navigation));
        sessionStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());
        if (process.env.NODE_ENV === 'development') {
          console.log('Cached TOC navigation');
        }
      } catch (cacheError) {
        console.warn('Error caching navigation:', cacheError);
      }

      return navigation;
    } catch (error) {
      // Handle specific error cases for better user experience
      let errorMessage = 'Unknown error';

      if (error.response) {
        const status = error.response.status;
        switch (status) {
          case 403:
            errorMessage = 'TOC.md file not found in S3 bucket or access denied';
            break;
          case 404:
            errorMessage = 'TOC.md file not found';
            break;
          default:
            errorMessage = `HTTP ${status}: ${error.response.data?.message || error.message}`;
        }
      } else {
        errorMessage = error.message;
      }

      console.warn('TOC.md not available, showing index page only:', errorMessage);

      // Return minimal navigation with just the index page if TOC.md doesn't exist
      const indexOnlyNav = [
        {
          title: 'Documentation',
          items: [
            { title: 'Home', url: 'index.md', slug: '', description: 'Documentation home page' }
          ]
        }
      ];

      if (process.env.NODE_ENV === 'development') {
        console.log('Using index-only navigation:', indexOnlyNav);
        console.log('💡 To enable full navigation, add TOC.md file to the S3 bucket');
      }

      return indexOnlyNav;
    }
  },

  /**
   * Fetch an image or other binary file from the documentation API
   * @param {string} filename - The filename/path of the image to fetch
   * @returns {Promise<Blob>} The image as a blob
   */
  fetchDocumentationImage: async (filename) => {
    try {
      const url = `/docs/${filename}`;

      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching documentation image from: ${url}`);
      }

      const response = await api.get(url, {
        responseType: 'blob' // Important: request as blob for binary data
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Documentation image response type:', typeof response.data);
        console.log('Response status:', response.status);
        console.log('Content-Type:', response.headers['content-type']);
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching documentation image:', error);

      // Log detailed error information for debugging
      if (error.response) {
        console.error(`documentationService: Image request failed with status ${error.response.status}`);
        console.error(`documentationService: Error response data:`, error.response.data);
        console.error(`documentationService: Response headers:`, error.response.headers);
      } else if (error.request) {
        console.error(`documentationService: No response received for image request`);
      } else {
        console.error(`documentationService: Error setting up image request:`, error.message);
      }

      throw error;
    }
  },

  /**
   * Extract image paths from markdown content
   * @param {string} markdown - Markdown content
   * @returns {Array<string>} Array of image paths
   */
  extractImagePaths: (markdown) => {
    const imagePaths = [];
    const imageRegex = /!\[([^\]]*)\]\(([^)]*)\)|<img([^>]*)\ssrc=["']([^"']+)["']([^>]*)>/gim;

    let match;
    while ((match = imageRegex.exec(markdown)) !== null) {
      let imageSrc;

      if (match[2]) {
        // Markdown syntax: ![alt](src)
        imageSrc = match[2];
      } else if (match[4]) {
        // HTML syntax: <img src="..." ...>
        imageSrc = match[4];
      }

      // Only include relative paths (not external URLs)
      if (imageSrc && !imageSrc.startsWith('http://') && !imageSrc.startsWith('https://')) {
        imagePaths.push(imageSrc);
      }
    }

    return [...new Set(imagePaths)]; // Remove duplicates
  },



  /**
   * Preload index.md and its images for immediate loading
   * This is called first during preloading to ensure the landing page loads instantly
   */
  preloadIndexWithImages: async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Preloading index.md with images...');
      }

      // Load index.md with front matter
      const indexData = await documentationService.fetchDocumentationWithFrontMatter('index.md');

      if (process.env.NODE_ENV === 'development') {
        console.log('Index.md preloaded successfully');
      }

      // Extract image paths from the index content
      const imagePaths = documentationService.extractImagePaths(indexData.content);

      if (imagePaths.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Preloading ${imagePaths.length} images from index.md:`, imagePaths);
        }

        // Preload images in parallel with small delays to avoid overwhelming the server
        const imagePromises = imagePaths.map((imagePath, index) =>
          new Promise(resolve => {
            setTimeout(async () => {
              try {
                const blob = await documentationService.fetchDocumentationImage(imagePath);
                await imageCache.store(imagePath, blob);
                if (process.env.NODE_ENV === 'development') {
                  console.log(`Preloaded and cached image: ${imagePath}`);
                }
              } catch (error) {
                console.warn(`Failed to preload image ${imagePath}:`, error.message);
              }
              resolve();
            }, index * 25); // 25ms delay between image requests
          })
        );

        await Promise.all(imagePromises);

        if (process.env.NODE_ENV === 'development') {
          console.log('Index.md images preloading completed');
        }
      }

      return indexData;
    } catch (error) {
      console.warn('Failed to preload index.md with images:', error.message);
      throw error;
    }
  },

  /**
   * Preload TOC and top-level documentation pages on login
   * This improves performance by caching essential docs in session storage
   */
  preloadDocumentation: async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Starting documentation preloading...');
      }

      // First, preload index.md and its images for immediate access
      await documentationService.preloadIndexWithImages();

      // Load TOC to get navigation structure
      const navigation = await documentationService.fetchTOC();

      // Extract top-level document URLs from navigation
      const topLevelDocs = [];

      navigation.forEach(category => {
        if (category.items) {
          category.items.forEach(item => {
            if (item.url && !item.children?.length) {
              topLevelDocs.push(item.url);
            }
          });
        }
      });

      // Remove index.md from the list since we already preloaded it
      const remainingDocs = topLevelDocs.filter(doc => doc !== 'index.md');

      // Preload remaining top-level documents (limit to first 4 to avoid overwhelming the server)
      const docsToPreload = remainingDocs.slice(0, 4);

      if (docsToPreload.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Preloading additional documents:', docsToPreload);
        }

        // Load documents in parallel but with a small delay to avoid overwhelming the server
        const preloadPromises = docsToPreload.map((docUrl, index) =>
          new Promise(resolve => {
            setTimeout(async () => {
              try {
                await documentationService.fetchDocumentationWithFrontMatter(docUrl);
                if (process.env.NODE_ENV === 'development') {
                  console.log(`Preloaded: ${docUrl}`);
                }
              } catch (error) {
                console.warn(`Failed to preload ${docUrl}:`, error.message);
              }
              resolve();
            }, index * 50); // 50ms delay between requests
          })
        );

        await Promise.all(preloadPromises);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Documentation preloading completed');
      }

    } catch (error) {
      console.warn('Error preloading documentation:', error);
      // Don't throw - preloading is optional and shouldn't block login
    }
  },

  /**
   * Flatten navigation for easier lookup
   * @param {Array} navigation - Navigation structure
   * @returns {Map} Map of slug to item
   */
  flattenNavigation: (navigation) => {
    return flattenNavigation(navigation);
  },






};

export default documentationService;
